/**
 * Survey Photo Upload (main CRA client)
 *
 * The signed-URL PUT transport now lives in the shared
 * `@officehub/survey-capture` package so this app and the Sales PWA can't drift
 * out of sync with the backend's upload contract (the v4 signed
 * `x-goog-content-length-range` header, timeout, and retry policy). This module
 * re-exports those primitives and adds the one app-specific piece: the
 * Firebase-bound `survey_getUploadUrl` callable (this app's `functions`
 * instance). Both `useSurveyIntake` (quick mobile capture) and `useSurveyPhotos`
 * (section photo upload) consume these.
 */

import { httpsCallable } from './firebase/authReadyCallable';
import { functions } from '../config/firebase';
// Import from the package's framework-free transport subpath (NOT the barrel):
// the barrel pulls in the React components (NoteCard/sections), which fail to
// resolve `react/jsx-runtime` from this package's dist in the client's Jest env
// (where the package has no node_modules). The transport entry has zero deps.
import type { GetUploadUrlResult } from '@officehub/survey-capture/signed-upload';

export {
  uploadBlobToSignedUrl,
  uploadBlobToSignedUrlWithRetry,
  SURVEY_UPLOAD_TIMEOUT_MS,
  SURVEY_CONTENT_LENGTH_RANGE_HEADER,
} from '@officehub/survey-capture/signed-upload';
export type {
  GetUploadUrlResult,
  UploadProgress,
  UploadBlobOptions,
} from '@officehub/survey-capture/signed-upload';

/**
 * Request a signed upload URL for a photo on an existing survey. App-bound:
 * uses this app's Firebase `functions` instance and callable routing.
 */
export async function getSurveyUploadUrl(
  surveyId: string,
  contentType: string,
): Promise<GetUploadUrlResult> {
  const getUploadUrlFn = httpsCallable<
    { surveyId: string; contentType?: string },
    GetUploadUrlResult
  >(functions, 'survey_getUploadUrl');
  const result = await getUploadUrlFn({ surveyId, contentType });
  return result.data;
}
